import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

const BRAND_COLORS = {
    primary: '#1a1a1a',
    accent: '#C9A961',
    light: '#F5F5F0',
    lightGray: '#E8E8E3',
};

const InstallPwaPrompt = ({ userId, onClose }) => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [shouldShow, setShouldShow] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();
            // Stash the event so it can be triggered later.
            setDeferredPrompt(e);
            // Also update global in case we unmount/remount
            window.deferredPwaPrompt = e;
        };

        window.addEventListener('beforeinstallprompt', handler);

        // Check if event was already captured globally before mount
        if (window.deferredPwaPrompt) {
            setDeferredPrompt(window.deferredPwaPrompt);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
        };
    }, []);

    useEffect(() => {
        if (userId && deferredPrompt) {
            // Check if user has already dismissed the prompt
            const hasDismissed = localStorage.getItem(`pwaPromptDismissed_${userId}`);
            if (!hasDismissed) {
                setShouldShow(true);
            }
        }
    }, [userId, deferredPrompt]);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;

        // Show the install prompt
        deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            console.log('User accepted the install prompt');
        } else {
            console.log('User dismissed the install prompt');
        }

        // We've used the prompt, and can't use it again, throw it away
        setDeferredPrompt(null);
        window.deferredPwaPrompt = null;
        setShouldShow(false);
        if (onClose) onClose(true);
    };

    const handleDismiss = () => {
        if (userId) {
            localStorage.setItem(`pwaPromptDismissed_${userId}`, 'true');
        }
        setShouldShow(false);
        if (onClose) onClose(false);
    };

    if (!shouldShow) return null;

    return (
        <div className="bg-white border-2 p-6 md:p-8 mb-8 relative" style={{ borderColor: BRAND_COLORS.primary }}>
            <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Dismiss"
            >
                <X className="w-5 h-5" />
            </button>

            <div className="flex items-start space-x-4 pr-8">
                <div className="flex-shrink-0">
                    <div className="w-12 h-12 border-2 rounded-full flex items-center justify-center" style={{ borderColor: BRAND_COLORS.accent }}>
                        <Download className="w-6 h-6" style={{ color: BRAND_COLORS.accent }} />
                    </div>
                </div>

                <div className="flex-1">
                    <h3 className="text-xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
                        Install SkillVend App
                    </h3>
                    <p className="text-gray-600 font-light leading-relaxed mb-4">
                        Install the SkillVend app for a better experience, faster access, and offline capabilities. Add it to your home screen today!
                    </p>

                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={handleInstallClick}
                            className="px-6 py-2.5 border-2 font-light tracking-wide hover:bg-opacity-90 transition-all"
                            style={{
                                backgroundColor: BRAND_COLORS.primary,
                                borderColor: BRAND_COLORS.primary,
                                color: 'white'
                            }}
                        >
                            Install App
                        </button>

                        <button
                            onClick={handleDismiss}
                            className="px-6 py-2.5 border-2 font-light tracking-wide hover:bg-gray-50 transition-all"
                            style={{
                                borderColor: BRAND_COLORS.lightGray,
                                color: BRAND_COLORS.primary
                            }}
                        >
                            Not Now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InstallPwaPrompt;
